<template>
  <div class="container-fluid payout-page">
    <div class="row">
      <div class="col-12">
        <div class="page-table-header full-height-page-wrapper">
          <PageHeader showBreadcrumbs page-title="Payouts">
            <template #buttons>
              <b-button
                class="button title-container__controls-item"
                variant="danger"
                @click="removeInBulk"
                :disabled="isDataPageLoading"
              >
                Delete All
              </b-button>
              <b-button
                class="button title-container__controls-item"
                variant="secondary"
                @click="exportPayouts"
                :disabled="isDataPageLoading"
              >
                <span class="material-icons icon-image-preview">
                  download
                </span>
                Export XLS
              </b-button>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowBulkCreation ? 'secondary' : 'info'"
                @click="toggleShowBulkCreation"
              >
                <span class="material-icons icon-image-preview"> tune </span>
                Bulk Creation
              </b-button>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview"> tune </span>
                Filters
              </b-button>
              <b-button
                variant="success"
                v-if="$can(permissions.CreateFinancesPayouts)"
                type="button"
                class="button"
                @click.prevent="showCreateModal"
                :disabled="isDataPageLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            ref="filtersComponent"
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="filters"
            :isLoading="isDataPageLoading && isInitialDataLoaded"
            :data-changes="true"
            resetToInitial
            @changes="checkChanges"
            @submit="setFilters"
          />

          <FinancePayoutsBulkCreation
            v-show="isShowBulkCreation"
            :templates="getPayoutsTemplateTypes"
            @updatePayouts="updatePayouts"
          />

          <div
            v-if="isDataPageLoading && !isInitialDataLoaded"
            class="content-loading-wrapper"
          >
            <Preloader class="centered-preloader" />
          </div>

          <div v-else class="full-height-content">

            <div v-if="getPayoutsTableData.length">
              <TableForPage
                ref="table"
                :tableData="getPayoutsTableData"
                :isDataLoading="isDataPageLoading && isInitialDataLoaded"
                :fields="getTableField"
                :perPage="getPayouts.per_page"
                :currentPage="getPayouts.current_page"
                :sortBy="sortField"
                :totalRows="getPayouts.total"
                :slots="table.slots"
                :tableRowModalFields="tableRowModal.fields"
                isServerRender
                noLocalSorting
                @update="fetchPayouts($event)"
              >
                <template v-slot:index="slotProps">
                  <IndexColumn
                    :index="slotProps.index"
                    :currentPage="getPayouts.current_page"
                    :itemsPerPage="getPayouts.per_page"
                    :isLoading="isDataPageLoading"
                  />
                </template>
                <template v-slot:name="slotProps">
                  <TableUserItem
                    v-if="slotProps.item.payee_type === 'contractor'"
                    :user="slotProps.item.contractor"
                  />
                  <TablePartnerItem
                    v-if="slotProps.item.payee_type === 'partner'"
                    :partnerName="slotProps.item.partner.company_name"
                    :contactPersonName="slotProps.item.partner.full_name"
                  />
                </template>
                <template v-slot:own_bank_details="slotProps">
                  <LabelBankCard
                    :card_details="slotProps.item.own_bank_details.summary"
                  />
                </template>
                <template v-slot:payee_bank_details="slotProps">
                  <LabelBankCard
                    :card_details="
                      slotProps.item.payee_bank_details
                        ? slotProps.item.payee_bank_details.summary
                        : ''
                    "
                  />
                </template>
                <template v-slot:payout_status="slotProps">
                  <PayoutStatus :status="slotProps.item.payout_status" />
                </template>
                <template v-slot:amount="slotProps">
                  {{ slotProps.item.amount }} {{ slotProps.item.currency.code }}
                </template>
                <template v-slot:col_actions="slotProps">
                  <PayoutsLinks
                    :templates="getPayoutsTemplateTypes"
                    :item="slotProps.item"
                    isRouteUpdate
                    @showEditPayoutsModal="
                      ($event) => {
                        showEditModal($event);
                        scrollToTheTop();
                      }
                    "
                    @markAsPaid="markPaid($event)"
                    @updatePayouts="updatePayouts"
                  />
                </template>
              </TableForPage>
              <div>
                <div class="mt-20px">
                  <b-card-group deck class="mb-3">
                    <MoneyTotalsCard
                      title="Original totals"
                      :data="totalAmount.original"
                    />
                    <MoneyTotalsCard
                      title="Equivalent totals"
                      :data="totalAmount.equivalent"
                    />
                  </b-card-group>
                </div>
              </div>
            </div>
            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && getPayoutsTableData.length === 0 && !getPayouts.isAnyData"
              customIcon="folder_off"
              customIconClasses="material-symbols-outlined empty-state__icon"
              title="No payouts yet"
              description="You haven't created any payouts yet. Start by adding one to manage your disbursements."
              buttonText="CREATE PAYOUTS"
              @button-click="handleCreateClick"
            />
            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && getPayoutsTableData.length === 0 && getPayouts.isAnyData"
              @reset-filters="handleResetFilters"
            />
          </div>

          <FinancePayoutsForm
            ref="financePayoutsFormCreate"
            v-if="!isOldEditForm"
            v-model="createModal.show"
            mode="create"
            :data="createModal.form"
            :title="createModal.title"
            @submit="createFinancePayouts"
            @input="onClose"
            @showDownloadModal="showDownloadPayoutModal"
            @loadingChange="handlePayoutFormLoadingChange"
            :breadcrumbs="[
              {
                text: 'Home',
                to: { name: 'home' },
              },
              {
                text: 'Payouts',
                click: () => hideCreateModal(),
              },
            ]"
          />
          <FinancePayoutsForm
            ref="financePayoutsFormEdit"
            v-if="!isOldEditForm"
            v-model="editModal.show"
            mode="edit"
            :data="editModal.form"
            :title="
              editModal.form && editModal.form.payout_number
                ? 'Payout ' + editModal.form.payout_number
                : editModal.title
            "
            @submit="editFinancePayouts"
            @input="onClose"
            @showDownloadModal="showDownloadPayoutModal"
            @loadingChange="handlePayoutFormLoadingChange"
            :breadcrumbs="[
              {
                text: 'Home',
                to: { name: 'home' },
              },
              {
                text: 'Payouts',
                click: () => {
                  hideEditModal();
                  onClose();
                },
              },
            ]"
          />

        <!-- Download Payout Modal -->
        <DownloadPayoutModal
          v-model="downloadPayoutModal.show"
          :invoice="downloadPayoutModal.payout"
          :templates="getAvailableTemplates()"
          :triggerElement="downloadPayoutModal.triggerElement"
          documentType="Payout"
          numberField="payout_number"
        />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import Moment from 'moment'
import { mapGetters } from 'vuex'
import { permissions } from '@mocks/permissions'
import { PostalNames } from '@/constants/PortalNames'
import Preloader from '@components/Preloader'
import TableForPage from '@tables/TableForPage'
import FinanceMixin from '../mixins/FinanceMixin'
import PageHeader from '@components/PageHeader'
import Filters from '@components/Filter/Filter'
import FinancePayoutsForm from '@components/modals/FinancePayoutsForm/FinancePayoutsForm.vue'
import DownloadPayoutModalMixin from '@mixins/DownloadPayoutModalMixin.js'
import TableUserItem from '@components/tables/TableUserItem'
import TablePartnerItem from '@components/tables/TablePartnerItem'
import LabelBankCard from '@components/tables/elements/LabelBankCard'
import PayoutStatus from '@components/tables/elements/PayoutStatus'
import IndexColumn from '@components/tables/elements/Index'
import PayoutsLinks from '../components/PayoutsLinks'
import FinancePayoutsBulkCreation from '@components/FinancePayoutsBulkCreation.vue'
import MoneyTotalsCard from '@components/MoneyTotalsCard'
import { getUrlSearchParams } from '@/functions/getUrlQueryParams'
import ScrollPositionMixin from '@mixins/ScrollPositionMixin'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  components: {
    Preloader,
    PageHeader,
    Filters,
    TableForPage,
    FinancePayoutsForm,
    TableUserItem,
    TablePartnerItem,
    LabelBankCard,
    PayoutStatus,
    PayoutsLinks,
    FinancePayoutsBulkCreation,
    MoneyTotalsCard,
    IndexColumn,
    EmptyStateNoResults
  },
  mixins: [FinanceMixin, ScrollPositionMixin, DownloadPayoutModalMixin],
  data () {
    const defaultSortingField = 'payee_name'
    const className = 'invoices-filters-field-wrapper'
    return {
      permissions,
      modalTitle: 'payout',
      isShowBulkCreation: false,
      isAllOptionsFilterSet: false,
      isPayoutsDataLoaded: false,
      PostalNames,
      table: {
        slots: {
          index: {
            name: 'index',
            comment: false
          },
          name: {
            name: 'name',
            comment: false
          },
          payout_status: {
            name: 'payout_status'
          },
          amount: {
            name: 'amount'
          },
          col_actions: {
            name: 'col_actions'
          }
        },
        fields: [
          {
            key: 'index',
            label: '#',
            sortable: false,
            class: 'column-index text-center'
          },
          {
            key: 'name',
            label: 'Payee',
            sortable: true
          },
          {
            key: 'payout_status',
            label: 'Status'
          },
          {
            key: 'issue_date',
            label: 'Issue date',
            sortable: true
          },
          {
            key: 'due_date',
            label: 'Due date',
            sortable: true
          },
          {
            key: 'paid_date',
            label: 'Paid date',
            sortable: true
          },
          {
            key: 'amount',
            label: 'Amount'
          },
          {
            key: 'col_actions',
            label: 'Actions',
            class: 'actions-column column-files column-icon text-left'
          }
        ],
        params: {
          defaultSortingField,
          filters: null,
          sorting: '&sort[' + defaultSortingField + ']=asc',
          currentPage: 1
        }
      },
      filters: {
        payout_number: {
          type: 'input',
          name: 'payout_number',
          label: 'Payout number',
          placeholder: 'Payout number',
          class: className
        },
        contractor: {
          type: 'select',
          name: 'contractor_id',
          label: 'Contractor',
          selectLabel: 'label',
          valueField: 'id',
          allowEmpty: true,
          options: [],
          value: [],
          class: className
        },
        partner: {
          type: 'select',
          name: 'partner_id',
          label: 'Partner',
          selectLabel: 'company_name',
          valueField: 'id',
          allowEmpty: true,
          options: [],
          value: [],
          class: className
        },
        payout_status: {
          type: 'select',
          name: 'payout_status_id',
          label: 'Payout status',
          selectLabel: 'display_name',
          valueField: 'id',
          options: [],
          class: className
        },
        own_bank_details: {
          type: 'multiselect',
          name: 'own_bank_details_id',
          nameItem: 'own_bank_details_id',
          label: 'Own bank details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: [],
          class: className
        },
        payee_bank_details: {
          type: 'multiselect',
          name: 'payee_bank_details_id',
          nameItem: 'payee_bank_details_id',
          label: 'Payee bank details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: [],
          class: className
        },
        date_from: {
          type: 'datepicker',
          name: 'date_from',
          label: 'Date from',
          format: 'dd/MM/yyyy',
          value: Moment().startOf('month').toISOString(),
          defaultValue: Moment().startOf('month').toISOString(),
          class: className
        },
        date_to: {
          type: 'datepicker',
          name: 'date_to',
          label: 'Date to',
          format: 'dd/MM/yyyy',
          value: Moment().endOf('month').toISOString(),
          defaultValue: Moment().endOf('month').toISOString(),
          class: className
        }
      }
    }
  },
  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isPayoutsDataLoaded
    },
    ...mapGetters({
      getPayouts: 'finances/getPayouts',
      getAllPartners: 'partners/getAllPartners',
      getContractorBankDetails: 'finances/getContractorBankDetails',
      getPartnerBankDetails: 'finances/getPartnerBankDetails',
      getOwnBankDetails: 'finances/getOwnBankDetails',
      getPayoutsStatuses: 'finances/getPayoutsStatuses',
      getPayoutsTemplateTypes: 'finances/getPayoutsTemplateTypes',
      getAllUsersWithTrashed: 'getAllUsersWithTrashed'
    }),
    getTableField () {
      return JSON.parse(JSON.stringify(this.table.fields))
    },
    getPayoutsTableData () {
      if (!this.getPayouts.data) return []

      return this.getPayouts.data.map((item) => {
        return {
          ...item,
          due_date: this._toLocalTime(item.due_date),
          paid_date: this._toLocalTime(item.paid_date),
          issue_date: this._toLocalTime(item.issue_date)
        }
      })
    },
    totalAmount () {
      return this.getPayouts.totalAmount || 0
    },
    tableParams () {
      let params = '?page=' + this.table.params.currentPage

      if (this.table.params.sorting) {
        params += this.table.params.sorting
      }
      if (this.table.params.filters) {
        params += this.table.params.filters
      }
      return params
    },
    sortField () {
      if (this.table.params.sorting) {
        let arr = this.table.params.sorting.match(/sort\[([a-zA-Z_]*)\]/)
        return arr[1] ?? this.table.params.defaultSortingField
      } else {
        return this.table.params.defaultSortingField
      }
    },
    isOldEditForm () {
      // TODO remove?
      return getUrlSearchParams().get('isOldEditForm') === 'true'
    }
  },
  watch: {
    'filters.contractor.value': {
      deep: true,
      handler (contractor) {
        this.filters.payee_bank_details.options = []
        if (contractor && Object.keys(contractor).length) {
          this.filters.partner.disabled = true
          this.filters.payee_bank_details.label = 'Contractor bank details'
          this.filters.payee_bank_details.options =
            this.getContractorBankDetails.filter((item) => {
              return item.contractor_id === contractor.id
            })
        } else {
          this.filters.partner.disabled = false
        }
        this.filters.payee_bank_details.value = null
      }
    },
    'filters.partner.value': {
      deep: true,
      handler (partner) {
        this.filters.payee_bank_details.options = []
        if (partner && Object.keys(partner).length) {
          this.filters.contractor.disabled = true
          this.filters.payee_bank_details.label = 'Partner bank details'
          this.filters.payee_bank_details.options =
            this.getPartnerBankDetails.filter((item) => {
              return item.partner_id === partner.id
            })
        } else {
          this.filters.contractor.disabled = false
        }
        this.filters.payee_bank_details.value = null
      }
    }
  },
  created () {
    this.isShowFilter = true
    this.isDataPageLoading = true

    Promise.all([
      this.$api.partners.getAllPartners(),
      this.$api.finances.getPayoutsStatuses(),
      this.$api.finances.getPayoutsTemplateTypes(),
      this.$api.finances.getPayoutsTypes()
    ])
      .then(() => {
        this.setAllOptionsFilter()
        this.setFilters(this.filters)
      })
  },
  methods: {
    onClose () {
      const route = this.$router.resolve({ name: 'finances-payouts' })
      this.restoreScrollPosition()
      window.history.pushState({}, '', route.href)
    },
    async fetchPayouts (params = null) {
      if (params) {
        this.table.params.currentPage = params.currentPage

        if (params.sortBy) {
          this.table.params.sorting =
            '&sort[' +
            params.sortBy +
            ']=' +
            (params.sortDesc ? 'desc' : 'asc')
        }
      }
      return this._toggleDataPageLoading(() =>
        this.$api.finances.getPayouts(this.tableParams)
      ).then((result) => {
        this.isPayoutsDataLoaded = true
        return result
      })
    },
    updatePayouts (data) {
      if (!data) return this.fetchPayouts()

      const { currentPage, sortDesc, sortBy } = data

      this.setParamsCurrentPage(currentPage)
      this.setParamsSort(sortDesc, sortBy)

      this.fetchPayouts()
    },
    setAllOptionsFilter () {
      this.filters.contractor.options = this.getAllUsersWithTrashed
      this.filters.partner.options = this.getAllPartners
      this.filters.own_bank_details.options = this.getOwnBankDetails
      this.filters.payout_status.options = this.getPayoutsStatuses

      this.isAllOptionsFilterSet = true
    },
    async setFilters (data) {
      const filterData = cloneDeep(data)
      filterData.date_from.value = this._toLocalTime(
        filterData.date_from.value,
        'YYYY-MM-DD'
      )
      filterData.date_to.value = this._toLocalTime(
        filterData.date_to.value,
        'YYYY-MM-DD'
      )
      await this.setParamsFilters(filterData)
      await this.setParamsCurrentPage()
      await this.fetchPayouts()
    },
    showEditModal (params) {
      if (this.$can(this.permissions.UpdateAllFinancesPayouts)) {
        this.editModal.show = true
        this.editModal.form = cloneDeep(params)
      }
    },
    async createFinancePayouts (data) {
      this._toggleDataPageLoading(async () => {
        try {
          await this.$api.finances.createPayouts(data)
          // Clear backend errors on success
          if (this.$refs.financePayoutsFormCreate) {
            this.$refs.financePayoutsFormCreate.clearAllBackendErrors()
          }
          this.hideCreateModal()
          this.updatePayouts()
        } catch (error) {
          // Handle backend validation errors
          if (error?.response?.data?.errors) {
            this.handleBackendValidationErrors(error, 'create')
          } else {
            // Re-throw non-validation errors to maintain existing error handling
            throw error
          }
        } finally {
          // Always turn off loading state
          if (this.$refs.financePayoutsFormCreate) {
            this.$refs.financePayoutsFormCreate.finishLoading()
          }
        }
      })
    },
    handleBackendValidationErrors (error, mode) {
      // Pass the backend errors to the appropriate form component
      const refName = mode === 'create' ? 'financePayoutsFormCreate' : 'financePayoutsFormEdit'
      const formRef = this.$refs[refName]

      if (error?.response?.data?.errors && formRef) {
        // Set backend errors directly on the form component (like FinanceInvoices pattern)
        formRef.backendErrors = error.response.data.errors
        // The watcher in the form component will auto-focus on the first error
      }
    },
    editFinancePayouts (data) {
      this._toggleDataPageLoading(async () => {
        try {
          await this.$api.finances.updatePayouts(data)
          // Clear backend errors on success
          if (this.$refs.financePayoutsFormEdit) {
            this.$refs.financePayoutsFormEdit.clearAllBackendErrors()
          }
          this.hideEditModal()
          this.updatePayouts()
        } catch (error) {
          // Handle backend validation errors
          if (error?.response?.data?.errors) {
            this.handleBackendValidationErrors(error, 'edit')
          } else {
            // Re-throw non-validation errors to maintain existing error handling
            throw error
          }
        } finally {
          // Always turn off loading state
          if (this.$refs.financePayoutsFormEdit) {
            this.$refs.financePayoutsFormEdit.finishLoading()
          }
        }
      })
    },
    async markPaid (payout) {
      this._toggleDataPageLoading(async () => {
        const marked = await this.$api.finances.markPayoutPaid(payout.id)
        if (marked.data) {
          this.$snotify.success(
            'Payout successfully marked as paid',
            'Success',
            this.$store.state.snotify
          )
          this.updatePayouts()
        }
      })
    },
    async removeInBulk () {
      this.isDataPageLoading = true
      try {
        const payouts = await this.$api.finances.getPayouts(this.tableParams, false)
        this.isDataPageLoading = false

        const payoutsData = payouts.data?.data || payouts.data
        if (!payoutsData || !Array.isArray(payoutsData) || payoutsData.length === 0) {
          this.$snotify.info('No payouts to delete', 'Info', this.$store.state.snotify)
          return
        }

        const removeItemsPreviews = payoutsData.map((item) => {
          return (
            item.payee_name +
            ' (' +
            Moment(item.issue_date).format('MMM DD YYYY') +
            ')'
          )
        })
        const payoutIds = payoutsData.map((item) => item.id)

        const confirm = await this.$root.$confirm.open(
          'Remove all payout from the list',
          'Are you sure you want to delete ' +
            removeItemsPreviews.length +
            ' payouts?<br>' +
            removeItemsPreviews.join('<br>')
        )

        if (!confirm) return

        this.isDataPageLoading = true
        try {
          const response = await this.$api.finances.removePayoutsInBulk({
            payout_ids: payoutIds
          })
          if (response.status === 200) {
            this.$snotify.success(
              response.data.message,
              'Success',
              this.$store.state.snotify
            )
          }
          this.updatePayouts()
        } finally {
          this.isDataPageLoading = false
        }
      } catch (error) {
        this.isDataPageLoading = false
        throw error
      }
    },
    checkChanges (fields) {
      this.filters = fields
      this.$forceUpdate()
    },
    toggleShowBulkCreation () {
      this.isShowBulkCreation = !this.isShowBulkCreation
    },
    exportPayouts () {
      this._toggleDataPageLoading(async () => {
        return this.axios
          .post('/api/finances/payouts/export', this.tableParams, {
            responseType: 'blob',
            headers: {
              Authorization: 'Bearer' + localStorage.getItem('access_token')
            }
          })
          .then((response) => {
            const fileURL = window.URL.createObjectURL(
              new Blob([response.data])
            )
            const fileLink = document.createElement('a')
            fileLink.href = fileURL
            const contentDisposition = response.headers['content-disposition']
            const fileNamePosition =
              contentDisposition.indexOf('filename=') + 9
            const fileName = contentDisposition.substr(fileNamePosition)

            fileLink.setAttribute('download', fileName)
            document.body.appendChild(fileLink)
            fileLink.click()
          })
      })
    },
    handleResetFilters () {
      if (this.$refs.filtersComponent) {
        this.$refs.filtersComponent.resetFilter()
      }
    },
    handleCreateClick () {
      this.showCreateModal()
    },
    handlePayoutFormLoadingChange (loading) {
      // This method handles loading state changes from the payout form
      // The loading state is now managed internally by the FinancePayoutsForm component
    }
  }
}
</script>

<style lang="scss" scoped>
.payout-page {
  ::v-deep th.actions-column {
    padding-left: 24px;
  }
}
</style>

<style lang="scss" scoped>
.payout-page .table__theme-default {
  table td {
    padding-top: 8px;
    padding-bottom: 3px;
  }

  .column-index {
    width: 15px;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
</style>
